import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getCompanies, createCompany, updateCompany, deleteCompany } from '../services/api';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import { useTranslation } from 'react-i18next';

function Companies() {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    oib: '',
    email: '',
    phone: '',
    website: '',
    notes: '',
    directors: [],
    ibans: [],
  });

  const columns = [
    { field: 'name', headerName: t('companies.fields.name'), width: 200 },
    { field: 'oib', headerName: t('companies.fields.oib'), width: 130 },
    { field: 'email', headerName: t('companies.fields.email'), width: 200 },
    { field: 'phone', headerName: t('companies.fields.phone'), width: 130 },
    { 
      field: 'directors', 
      headerName: t('companies.fields.directors'), 
      width: 200,
      renderCell: (params) => (
        <Box>
          {params.row.directors?.map((director, index) => (
            <Typography key={index} variant="body2">
              {director.name}
            </Typography>
          ))}
        </Box>
      )
    },
    { 
      field: 'ibans', 
      headerName: t('companies.fields.ibans'), 
      width: 150,
      renderCell: (params) => (
        <Box>
          {params.row.ibans?.length > 0 && (
            <Typography variant="body2">
              {t('companies.ibanCount', { count: params.row.ibans.length })}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button onClick={() => handleEdit(params.row)}>{t('common.edit')}</Button>
          <Button color="error" onClick={() => handleDelete(params.row.id)}>{t('common.delete')}</Button>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingCompany(null);
    setFormData({
      name: '',
      address: '',
      oib: '',
      email: '',
      phone: '',
      website: '',
      notes: '',
      directors: [],
      ibans: [],
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCompany(null);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      address: company.address,
      oib: company.oib,
      email: company.email || '',
      phone: company.phone || '',
      website: company.website || '',
      notes: company.notes || '',
      directors: company.directors || [],
      ibans: company.ibans || [],
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('companies.deleteConfirm'))) {
      try {
        await deleteCompany(id);
        fetchCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, formData);
      } else {
        await createCompany(formData);
      }
      handleClose();
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addDirector = () => {
    setFormData({
      ...formData,
      directors: [...formData.directors, { name: '', phone: '', email: '' }]
    });
  };

  const removeDirector = (index) => {
    setFormData({
      ...formData,
      directors: formData.directors.filter((_, i) => i !== index)
    });
  };

  const updateDirector = (index, field, value) => {
    const updatedDirectors = [...formData.directors];
    updatedDirectors[index] = { ...updatedDirectors[index], [field]: value };
    setFormData({ ...formData, directors: updatedDirectors });
  };

  const addIban = () => {
    setFormData({
      ...formData,
      ibans: [...formData.ibans, { name: '', iban: '' }]
    });
  };

  const removeIban = (index) => {
    setFormData({
      ...formData,
      ibans: formData.ibans.filter((_, i) => i !== index)
    });
  };

  const updateIban = (index, field, value) => {
    const updatedIbans = [...formData.ibans];
    updatedIbans[index] = { ...updatedIbans[index], [field]: value };
    setFormData({ ...formData, ibans: updatedIbans });
  };

  return (
    <ResponsivePageSection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography variant="h4">{t('companies.title')}</Typography>
        <Button variant="contained" onClick={handleOpen}>{t('companies.addCompany')}</Button>
      </Box>

      {isMobile ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
        <ResponsiveDataList
          rows={companies}
          getTitle={(c) => c.name}
          getSubtitle={(c) => [c.oib, c.email].filter(Boolean).join(' · ') || undefined}
          renderFields={(c) => (
            <Stack spacing={0.5}>
              {c.phone ? (
                <Typography variant="body2"><b>{t('companies.fields.phone')}:</b> {c.phone}</Typography>
              ) : null}
              {c.directors?.length ? (
                <Typography variant="body2">
                  <b>{t('companies.fields.directors')}:</b>{' '}
                  {c.directors.map((d) => d.name).filter(Boolean).join(', ')}
                </Typography>
              ) : null}
              {c.ibans?.length ? (
                <Typography variant="body2"><b>{t('companies.fields.ibans')}:</b> {c.ibans.length}</Typography>
              ) : null}
            </Stack>
          )}
          renderActions={(c) => (
            <>
              <Button size="small" onClick={() => handleEdit(c)}>{t('common.edit')}</Button>
              <Button size="small" color="error" onClick={() => handleDelete(c.id)}>{t('common.delete')}</Button>
            </>
          )}
        />
        )
      ) : (
        <DataGrid
          rows={companies}
          columns={columns}
          pageSize={10}
          loading={loading}
          autoHeight
        />
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingCompany ? t('companies.editCompany') : t('companies.addCompany')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  label={t('companies.fields.companyName')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="address"
                  label={t('companies.fields.address')}
                  value={formData.address}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="oib"
                  label={t('companies.fields.oib')}
                  value={formData.oib}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="website"
                  label={t('companies.fields.website')}
                  value={formData.website}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="email"
                  label={t('companies.fields.email')}
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="phone"
                  label={t('companies.fields.phone')}
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Directors Section */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">{t('companies.fields.directors')}</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addDirector}
                  variant="outlined"
                  size="small"
                >
                  {t('companies.addDirector')}
                </Button>
              </Box>
              {formData.directors.map((director, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={t('companies.fields.directorName')}
                        value={director.name}
                        onChange={(e) => updateDirector(index, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={t('companies.fields.phone')}
                        value={director.phone}
                        onChange={(e) => updateDirector(index, 'phone', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label={t('companies.fields.email')}
                        type="email"
                        value={director.email}
                        onChange={(e) => updateDirector(index, 'email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => removeDirector(index)}
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* IBANs Section */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">{t('companies.fields.ibans')}</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addIban}
                  variant="outlined"
                  size="small"
                >
                  {t('companies.addIban')}
                </Button>
              </Box>
              {formData.ibans.map((iban, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={t('companies.fields.ibanNameDescription')}
                        value={iban.name}
                        onChange={(e) => updateIban(index, 'name', e.target.value)}
                        placeholder={t('companies.ibanPlaceholder')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        fullWidth
                        label={t('companies.fields.ibanNumber')}
                        value={iban.iban}
                        onChange={(e) => updateIban(index, 'iban', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => removeIban(index)}
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>

            <TextField
              fullWidth
              name="notes"
              label={t('companies.fields.notes')}
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </ResponsivePageSection>
  );
}

export default Companies; 